import type { AgentFeedbackSummary, OwnerAnalytics, SharedQuestion } from '@ralph/contracts'
import type { I18n } from '@/i18n/index.ts'

/**
 * Turning the owner's numbers into the sentences an owner can act on (PRD-18 AGM-120).
 *
 * Pure on purpose: every judgement about what a number MEANS lives here, where it can be
 * tested, rather than inside JSX where it can only be eyeballed. The judgements are:
 *
 *  - a rate the server could not measure is not zero. `null` means "not enough went through
 *    yet to say", and rendering it as 0% would tell an owner their agent never fails when in
 *    fact nobody has asked it anything;
 *  - a non-zero rate below one percent rounds to "<1%", never to "0%", for the same reason;
 *  - tallies with no feedback at all have no helpful share — a 0/0 rendered as 0% reads as
 *    "everyone hated it".
 *
 * The percent SIGN is not a suffix this module owns, so the rate goes through `Intl` with the
 * locale the caller is reading in: its position and the space before it are the locale's
 * business, not ours (PRD-27 LOC-020). The judgements above are unchanged.
 */

/** What a rate needs in order to render itself in the reader's language. */
type Rates = Pick<I18n, 'formatPercent'>

/** `null` when the underlying rate was not measurable — the caller renders its own sentence. */
export const formatRate = (fmt: Rates, rate: number | null | undefined): string | null => {
  if (rate === null || rate === undefined) return null
  if (rate <= 0) return fmt.formatPercent(0, 0)
  if (rate >= 1) return fmt.formatPercent(1, 0)
  // Below one percent but not zero: "<1%" with the 1 formatted, so the sign sits where the
  // locale puts it rather than where this template does.
  if (rate * 100 < 1) return `<${fmt.formatPercent(0.01, 0)}`
  return fmt.formatPercent(rate, 0)
}

export type FeedbackTotals = {
  helpful: number
  unhelpful: number
  total: number
  /** Share of ratings that were helpful, or null when nobody has rated anything yet. */
  helpfulShare: string | null
}

export const feedbackTotals = (fmt: Rates, feedback: OwnerAnalytics['feedback']): FeedbackTotals => {
  const total = feedback.helpful + feedback.unhelpful
  return {
    helpful: feedback.helpful,
    unhelpful: feedback.unhelpful,
    total,
    helpfulShare: total === 0 ? null : formatRate(fmt, feedback.helpful / total)
  }
}

export type VersionFeedbackRow = {
  /** null = feedback that arrived before the agent was ever published under a version. */
  definitionVersion: number | null
  helpful: number
  unhelpful: number
  total: number
}

/**
 * Feedback by version, newest first.
 *
 * The order matters more than it looks: "did my last change make this better" is the one
 * question this table answers, and it is only readable when the version just published sits
 * at the top next to the one before it. Rows with no version sort last — they predate
 * publication and cannot be compared against anything.
 */
export const versionFeedbackRows = (summary: AgentFeedbackSummary | undefined): VersionFeedbackRow[] => {
  if (!summary) return []
  return [...summary.byVersion]
    .map((row) => ({ ...row, total: row.helpful + row.unhelpful }))
    .sort((a, b) => {
      if (a.definitionVersion === null) return 1
      if (b.definitionVersion === null) return -1
      return b.definitionVersion - a.definitionVersion
    })
}

/**
 * Shared questions, open ones first and newest within each group.
 *
 * The open ones are the improvement queue; the resolved ones are history. Interleaving them
 * by date would bury the three things an owner still has to answer under thirty they already
 * did.
 */
export const sortSharedQuestions = (questions: SharedQuestion[]): SharedQuestion[] =>
  [...questions].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'open' ? -1 : 1
    return Date.parse(b.createdAt) - Date.parse(a.createdAt)
  })

export const openQuestionCount = (questions: SharedQuestion[]): number =>
  questions.filter((question) => question.status === 'open').length

/** Health events newest first — an owner reads the current state, then how it got there. */
export const sortHealthEvents = (events: OwnerAnalytics['healthEvents']): OwnerAnalytics['healthEvents'] =>
  [...events].sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
